import { useEffect, useMemo, useState } from 'react';
import { api } from '../api';
import type { PlantDto } from '../types';
import { Card, Empty, ErrorBanner, Loader, PlantAvatar, StatusBadge, daysAgo } from '../components/common';

type Filter = 'all' | 'attention' | 'healthy' | 'treatment';

const FILTERS: { key: Filter; label: string }[] = [
  { key: 'all', label: 'Все' },
  { key: 'attention', label: 'Требуют внимания' },
  { key: 'healthy', label: 'Здоровые' },
  { key: 'treatment', label: 'Лечение' },
];

interface Props {
  spaceId: string;
  onOpenPlant: (plantId: string) => void;
  onAdd: () => void;
}

export function Plants({ spaceId, onOpenPlant, onAdd }: Props) {
  const [plants, setPlants] = useState<PlantDto[]>([]);
  const [filter, setFilter] = useState<Filter>('all');
  const [room, setRoom] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api
      .plants(spaceId)
      .then((items) => {
        setPlants(items);
        setError(null);
      })
      .catch((e) => setError(e instanceof Error ? e.message : 'Ошибка загрузки'))
      .finally(() => setLoading(false));
  }, [spaceId]);

  const rooms = useMemo(() => {
    const set = new Set<string>();
    plants.forEach((p) => p.locationText && set.add(p.locationText));
    return Array.from(set);
  }, [plants]);

  const visible = plants.filter((plant) => {
    if (room !== 'all' && plant.locationText !== room) return false;
    if (filter === 'all') return true;
    if (filter === 'attention') return plant.status === 'attention' || plant.attentionScore >= 30;
    return plant.status === filter;
  });

  if (loading) return <Loader />;

  return (
    <div>
      <div className="row between">
        <h1>Растения</h1>
        <button className="btn" onClick={onAdd}>
          + Добавить
        </button>
      </div>

      {error ? <ErrorBanner message={error} /> : null}

      <div className="filters">
        {FILTERS.map((item) => (
          <button
            key={item.key}
            className={filter === item.key ? 'active' : ''}
            onClick={() => setFilter(item.key)}
          >
            {item.label}
          </button>
        ))}
      </div>

      {rooms.length > 1 ? (
        <div className="filters">
          <button className={room === 'all' ? 'active' : ''} onClick={() => setRoom('all')}>
            Везде
          </button>
          {rooms.map((value) => (
            <button key={value} className={room === value ? 'active' : ''} onClick={() => setRoom(value)}>
              {value}
            </button>
          ))}
        </div>
      ) : null}

      {!visible.length ? (
        <Empty
          title={plants.length ? 'Ничего не подходит под фильтр' : 'Коллекция пуста'}
          hint={plants.length ? undefined : 'Добавьте первое растение — в боте или кнопкой выше.'}
        />
      ) : null}

      {visible.map((plant) => (
        <Card key={plant.id}>
          <div className="row" onClick={() => onOpenPlant(plant.id)} style={{ cursor: 'pointer' }}>
            <PlantAvatar url={plant.photoUrl} name={plant.displayName} />
            <div className="grow">
              <div className="row between">
                <span className="title">{plant.displayName}</span>
                <StatusBadge status={plant.status} />
              </div>
              <div className="hint">{plant.speciesText ?? 'вид не указан'}</div>
              <div className="hint">
                Полив: {daysAgo(plant.lastWateredAt)}
                {plant.nextTask ? ` · дальше: ${plant.nextTask.title}` : ''}
              </div>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}
